import h from 'hyperscript';
import { html, render } from 'lit-html';
import { PipsMode } from 'nouislider';

import { assert, requireNonNullable } from '@disy/lib/custom-error';
import { debounce } from '@disy/lib/debounce';
import { getTextWidth } from '@disy/lib/fonts';
import { clamp } from '@disy/lib/math';
import { px } from '@disy/lib/px';
import { FormElement } from '@disy/ui/form-element';

import { createSlider } from 'ui/slider/slider';

import './slider-number-input.css';

const COMPONENT_NAME = 'd-slider-number-input';

/**
 * Range for the slider.
 * Can specify min and max values, and can also include intermediary values.
 * The intermediary values keys represent the relative position (percentage) they occupy on the slider.
 * Non-linear ranges are possible, see the example in the catalog page.
 */
type Range = {
  /** The minimum value for the slider */
  min?: number;
  /** The maximum value for the slider */
  max?: number;
} & Record<number, number>;

const DEFAULT_RANGE = { min: 0, max: 100 };

function normalizeRange({ min = DEFAULT_RANGE.min, max = DEFAULT_RANGE.max, ...rest }: Range = {}): Required<Range> {
  assert(min < max, `The min value must be smaller than the max value`);
  return { min, ...rest, max };
}

export class SliderNumberInput extends FormElement<number> {
  readonly #numberInput = this.#createNumberInput();
  readonly #unitSpan = h(`span.${COMPONENT_NAME}--unit`);
  readonly #slider = this.#createSlider();

  #initialOptions;

  /**
   * @param options - Options
   * @param options.id - ID of the element
   * @param options.name - Name of the element (for form integration)
   * @param options.disabled - Should it be disabled
   * @param options.range - Range of values for the input. If the range is omitted (or one of min/max is omitted) the default range of [0,100 will be used.
   * @param options.defaultValue - initial value of slider. If omitted, the minimum value of the range will be used.
   * @param options.unit - unit to be appended to slider value. E.g '%' or 'px'
   * @param options.ticksDensity=10 - Defines how many ticks/pips are shown on the slider. These are
   * the vertical lines that divide the full slider range up into smaller parts. This value is a percentage (without
   * "%") indicating at what interval/portion of the full range a tick should be shown. The default shows 10 ticks.
   * @param options.tickLabels - Whether to show tick labels
   */
  constructor({
    defaultValue,
    id,
    name,
    disabled,
    tickLabels,
    ...options
  }: {
    defaultValue?: number;
    id?: string;
    name?: string;
    range?: Range;
    disabled?: boolean;
    tickLabels?: boolean;
    ticksDensity?: number;
    unit?: string;
  } = {}) {
    super({ defaultValue, id, name, disabled, valueFromString: Number.parseFloat });
    this.#initialOptions = options;
    if (!this.hasAttribute('tick-labels')) {
      this.toggleAttribute('tick-labels', Boolean(tickLabels));
    }
  }

  override connectedCallback() {
    const initialOptions = this.#initialOptions;
    this.#initialOptions = {};

    // Get option values from constructor-provided options object, with fallback to attributes and to default values
    const ticksDensity = initialOptions.ticksDensity ?? getAttributeAsIntIfPresent(this, 'ticks-density') ?? 10;
    const unit = initialOptions.unit ?? this.getAttribute('unit') ?? '';

    const initialRange = initialOptions.range;
    const normalizedRange = normalizeRange(
      initialRange ?? {
        min: getAttributeAsIntIfPresent(this, 'min'),
        max: getAttributeAsIntIfPresent(this, 'max'),
      },
    );

    if (initialRange || !this.hasAttribute('min')) {
      this.setAttribute('min', String(normalizedRange.min));
    }

    if (initialRange || !this.hasAttribute('max')) {
      this.setAttribute('max', String(normalizedRange.max));
    }

    this.#slider.updateOptions(
      {
        range: normalizedRange,
        pips: {
          mode: PipsMode.Positions, // labels only at the given percentage-based positions
          values: Object.keys(normalizedRange).map((key) => ({ min: 0, max: 100 })[key] ?? Number(key)),
          density: ticksDensity,
          format: unit ? { to: (valueIndex: number) => `${valueIndex}${unit}` } : undefined,
        },
      },
      false,
    );

    this.#numberInput.min = String(this.min);
    this.#numberInput.max = String(this.max);
    this.#unitSpan.textContent = unit;

    super.connectedCallback();

    this.setAttribute('role', 'group');
    this.classList.add(COMPONENT_NAME, 'd-stack-h', 'space-4');

    render(
      html`
        ${this.#slider.target}
        <div class="${COMPONENT_NAME}--number-input-container">
          ${this.#numberInput} ${unit ? this.#unitSpan : undefined}
        </div>
      `,
      this,
    );
  }

  override focus() {
    const slider = requireNonNullable(this.querySelector<HTMLElement>('[role="slider"]'));
    // focus the handle to be able to interact with the slider also by keyboard
    slider.focus();
  }

  protected override applyDisabled(disabled: boolean) {
    this.#numberInput.disabled = disabled;
    this.#slider.target.toggleAttribute('disabled', disabled);
  }

  protected override sanitizeValue(value: number) {
    // when the user interacts with the number input, the input might be empty, which causes the valueAsNumber to be NaN.
    // In this case we use the min value.
    // If a value is present, we round it (as the slider gives us numbers with many decimals) and we clamp the value to the [min, max] range.
    return Number.isNaN(value) ? this.min : clamp(Math.round(value), this.min, this.max);
  }

  protected override applyValue(value: number) {
    this.#slider.set(value);
    if (document.activeElement !== this.#numberInput) {
      if (Number.isFinite(value)) {
        this.#numberInput.valueAsNumber = value;
      } else {
        this.#numberInput.value = '';
      }
    }
    this.#unitSpan.style.left = px(getTextWidth(this.#numberInput.value));
  }

  #createNumberInput() {
    return h<HTMLInputElement>(`input.form-control.${COMPONENT_NAME}--number-input`, {
      type: 'number',
      oninput: (event: Event) => {
        event.stopPropagation();
        this.value = this.#numberInput.valueAsNumber;
        this.#dispatchInputEvent();
      },
      onchange: (event: Event) => {
        event.stopPropagation();
        this.setValue(this.#numberInput.valueAsNumber);
      },
    });
  }

  #createSlider() {
    const slider = createSlider(h(`.${COMPONENT_NAME}--slider`), {
      animationDuration: 100,
      range: DEFAULT_RANGE,
      start: 0,
      step: 1,
      keyboardSupport: true,
    });

    let arrowKeyPressed = false;
    slider.target.addEventListener(
      'keydown',
      (event: KeyboardEvent) => {
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.code)) {
          arrowKeyPressed = true;
        }
      },
      { capture: true },
    );

    // The 'slide' event is dispatched whenever then slider moves, by pointer or keyboard.
    // We dispatch an 'input' event for every 'slide' event.
    slider.on('slide', () => {
      this.value = slider.get(true) as number;
      this.#dispatchInputEvent();
    });

    const debouncedSetValue = debounce((value) => this.setValue(value));
    slider.on('change', () => {
      // When using the mouse, the slider dispatches a 'change' event once the dragging of the slider stops (handle released by the user).
      // When using the keyboard (arrow keys), the slider dispatches a 'change' event for every keystroke.
      // This component dispatches a change event for every change event caused by dragging, but when using the keyboard we debounce the events,
      // because we don't want to dispatch a large number of change events that might trigger re-renders of the UI
      // and other updates including backend HTTP requests.
      const value = slider.get(true) as number;
      if (arrowKeyPressed) {
        debouncedSetValue(value);
        arrowKeyPressed = false;
      } else {
        this.setValue(value);
      }
    });

    return slider;
  }

  #dispatchInputEvent() {
    return this.dispatchEvent(new CustomEvent('input', { bubbles: true }));
  }
}

customElements.define(COMPONENT_NAME, SliderNumberInput);

function getAttributeAsIntIfPresent(element: HTMLElement, attributeName: string) {
  return element.hasAttribute(attributeName) ? Number.parseInt(element.getAttribute(attributeName)!) : undefined;
}
